/**
 * WHAT FINANCE BILLED AGAINST WHAT THE SOFTWARE TURNED ON — the comparison
 * itself, apart from the table that draws it.
 *
 * Pure and separately tested, because the two rules below are both rules a
 * reader of the table cannot check by eye: one row that must never appear, and
 * one that must appear differently from the row it otherwise looks identical to.
 */

/**
 * `addl_location` IS NOT A PACKAGE.
 *
 * It is a plan-type SKU in the billing catalog (`type: 'plan'`, no `packageId`)
 * that prices a STORE. It belongs on the PlanAssignment — that line is what
 * charges for an extra location — and billing-service deliberately keeps it out
 * of `tenant.enabledPackages`: there is no feature behind it for a merchant to
 * switch on, and when it did leak in it rendered on their Packages tab beside
 * things like Ruby Vault.
 *
 * So "billed but not enabled" is its CORRECT state, and reporting that as drift
 * put a permanent red row on every multi-store tenant.
 *
 * Mirrors `ADDL_LOCATION_SKU_ID` in billing-service's `onboardingPricing.ts` and
 * platform-app's `LocationSubscriptionCard.tsx`; separate repos, so this is a
 * hand-kept copy.
 */
export const ADDL_LOCATION_SKU_ID = 'addl_location';

export interface ComparisonRow {
  id: string;
  /** The package's name, or the raw id when nothing can name it. */
  label: string;
  /**
   * The id names no package the catalog knows — a package deleted out from
   * under a tenant that still holds it. `enabledPackages` is grant-only, so
   * such a grant survives for ever and shows as drift that no activation can
   * resolve. A DIFFERENT finding from "enabled but not billed", and it must not
   * look the same.
   */
  unknown: boolean;
  inBilled: boolean;
  inEnabled: boolean;
  mismatch: boolean;
}

export interface ComparisonArgs {
  enabledPackages: readonly string[];
  /** `null` — the active plan assignment could not be read. NOT the same as `[]`. */
  billedPackages: readonly string[] | null;
  packageNames: Record<string, string>;
  /**
   * Whether the package catalog has actually loaded. While it has not, nothing
   * can be called deleted — an in-flight catalog would otherwise report the
   * tenant's whole estate as missing.
   */
  catalogLoaded: boolean;
}

/** Everything the comparison ignores, on either side. */
const comparable = (ids: readonly string[]): string[] =>
  ids.filter((id) => !!id && id !== ADDL_LOCATION_SKU_ID);

export function buildComparison(args: ComparisonArgs): ComparisonRow[] {
  const billedKnown = args.billedPackages !== null;
  const enabledSet = new Set(comparable(args.enabledPackages || []));
  const billedSet = new Set(comparable(billedKnown ? args.billedPackages! : []));

  const ids = Array.from(new Set([...enabledSet, ...billedSet]));

  return ids
    .map((id): ComparisonRow => {
      const name = args.packageNames[id];
      const inBilled = billedKnown && billedSet.has(id);
      const inEnabled = enabledSet.has(id);
      return {
        id,
        label: name || id,
        unknown: args.catalogLoaded && !name,
        inBilled,
        inEnabled,
        // With no billed side there is nothing to disagree WITH. Reporting every
        // enabled package as a mismatch would turn a failed read into a
        // fabricated finding.
        mismatch: billedKnown && inBilled !== inEnabled,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * What running `activate_packages` would actually change: the billed packages
 * the tenant is not yet holding.
 *
 * It can only ever ADD — never used to explain away an enabled-but-not-billed
 * row, and never offering `addl_location`, which billing-service now strips on
 * its own side too.
 */
export function activatableIds(args: {
  enabledPackages: readonly string[];
  billedPackages: readonly string[] | null;
}): string[] {
  if (args.billedPackages === null) return [];
  const enabledSet = new Set(comparable(args.enabledPackages || []));
  return comparable(args.billedPackages).filter((id) => !enabledSet.has(id));
}
